import { Component, Input, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Icons, LsqValidators } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { Observable, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { SupportedBankCountry, SupportedBankTransferType } from '../../../constants/bank.constants';
import { AddBankAccountFormService } from './add-bank-account-form.service';
import { AddIntlBankAccountFormService } from './add-intl-bank-account-form.service';
import { SupportedBankPaymentMethod } from './bank-accounts.models';
import { ROUTING_NUMBER_INFO_URL } from '../constants';

@Component({
    selector: 'add-intl-bank-account-form',
    styleUrls: ['./add-intl-bank-account-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <div class="add-intl-bank-account-form">
            <div class="international-form-container">
                <form [formGroup]="addBankAccountFormGroup$ | async" class="banking-info-form row">
                    <div class="col-12">
                        <lsq-input
                            *ngIf="bankPaymentMethod && bankPaymentMethod.country !== usEnum"
                            formControlName="accountName"
                            [name]="'account-name'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label="Bank account name input"
                            label="Bank Account Description"
                        >
                        </lsq-input>
                        <lsq-input
                            *ngIf="bankPaymentMethod && bankPaymentMethod.country === usEnum"
                            formControlName="accountName"
                            [name]="'account-name'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label="Bank account name input"
                            label="Exact Name of Account"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-md-6">
                        <lsq-input
                            formControlName="accountNumber"
                            [name]="'account-number'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label="Bank account number input"
                            label="Account Number"
                            [maxLength]="accountNumberMaxLength"
                            [noPaste]="true"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-md-6">
                        <lsq-input
                            formControlName="verifyAccountNumber"
                            [name]="'verify-account-number'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label="Bank account number verify input"
                            label="Verify Account Number"
                            [maxLength]="accountNumberMaxLength"
                            [noPaste]="true"
                        >
                        </lsq-input>
                    </div>
                    <div
                        *ngIf="bankPaymentMethod && bankPaymentMethod.country === usEnum"
                        class="col-md-6"
                    >
                        <lsq-input
                            formControlName="routingNumber"
                            [name]="'routingNumber'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label="Bank account routing number input"
                            label="ABA / Routing Number"
                            [maxLength]="usaRoutingNumberLength"
                            [type]="'number'"
                            [digitsOnly]="true"
                        >
                        </lsq-input>
                    </div>
                    <div
                        *ngIf="bankPaymentMethod && bankPaymentMethod.country === usEnum"
                        class="col-md-6"
                    >
                        <lsq-input
                            formControlName="bankName"
                            [name]="'bank-name'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label="Bank name input"
                            label="Bank Name"
                        >
                        </lsq-input>
                    </div>

                    <div
                        class="col-12 section-header"
                        [style.margin-bottom]="'0.7rem'"
                        [style.margin-top]="'2rem'"
                        i18n
                    >
                        Receiving / Beneficiary Bank Information:
                    </div>
                    <div class="col-12 bank-information-instructions" i18n>
                        Please provide detailed information on the receiving bank. Missing
                        information may lead to delays in processing payment(s).
                    </div>
                    <div
                        *ngIf="bankPaymentMethod && bankPaymentMethod.country !== usEnum"
                        class="col-12"
                    >
                        <lsq-input
                            formControlName="swiftCode"
                            [name]="'swift-code'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Swift Code"
                        >
                        </lsq-input>
                    </div>
                    <div
                        *ngIf="bankPaymentMethod && bankPaymentMethod.country === chinaEnum"
                        class="col-12"
                    >
                        <lsq-input
                            formControlName="routingNumber"
                            [name]="'routingNumber'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="CNAPS Code"
                        >
                        </lsq-input>
                    </div>
                    <div
                        *ngIf="bankPaymentMethod && bankPaymentMethod.country === indiaEnum"
                        class="col-12"
                    >
                        <lsq-input
                            formControlName="routingNumber"
                            [name]="'routingNumber'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="IFSC Code"
                        >
                        </lsq-input>
                    </div>
                    <div
                        *ngIf="bankPaymentMethod && bankPaymentMethod.country === canadaEnum"
                        class="col-12"
                    >
                        <lsq-input
                            formControlName="routingNumber"
                            [name]="'routingNumber'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Canadian Transit Number"
                        >
                        </lsq-input>
                    </div>
                    <div
                        *ngIf="bankPaymentMethod && bankPaymentMethod.country !== usEnum"
                        class="col-12"
                    >
                        <lsq-input
                            formControlName="bankName"
                            [name]="'bank-name'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label="Bank name input"
                            label="Bank Name"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12">
                        <lsq-input
                            formControlName="address"
                            [name]="'address'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Address"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12">
                        <lsq-input
                            formControlName="address2"
                            [name]="'address2'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Address 2"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12">
                        <lsq-input
                            formControlName="city"
                            [name]="'city'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="City / Locality"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-md-7">
                        <lsq-input
                            formControlName="state"
                            [name]="'state'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="State / Province"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-md-5" [style.margin-bottom]="'2rem'">
                        <lsq-input
                            formControlName="postal"
                            [name]="'postal'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Postal Code"
                        >
                        </lsq-input>
                    </div>

                    <div class="col-12 section-header" [style.margin-bottom]="'0.7rem'" i18n>
                        Beneficiary Information:
                    </div>
                    <div class="col-12 bank-information-instructions" i18n>
                        Enter the information exactly as it appears at your bank.
                    </div>

                    <div class="col-12" [style.margin-bottom]="'2rem'">
                        <lsq-checkbox
                            formControlName="useAddress"
                            i18n-label
                            label="Use the same information as my business"
                        ></lsq-checkbox>
                    </div>
                    <div class="col-12">
                        <lsq-input
                            formControlName="beneName"
                            [name]="'bene-name'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Exact Name on Account"
                        >
                        </lsq-input>
                    </div>

                    <div class="col-12">
                        <lsq-input
                            formControlName="beneAddress"
                            [name]="'bene-address'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Address"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12">
                        <lsq-input
                            formControlName="beneAddress2"
                            [name]="'bene-address2'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Address 2"
                        >
                        </lsq-input>
                    </div>

                    <div class="col-12">
                        <lsq-input
                            formControlName="beneCity"
                            [name]="'bene-city'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="City / Locality"
                        >
                        </lsq-input>
                    </div>

                    <div class="col-md-7">
                        <lsq-input
                            formControlName="beneState"
                            [name]="'bene-state'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="State / Province"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-md-5" [style.margin-bottom]="'2rem'">
                        <lsq-input
                            formControlName="benePostal"
                            [name]="'bene-postal'"
                            [group]="addBankAccountFormGroup$ | async"
                            i18n-label
                            label="Postal Code"
                        >
                        </lsq-input>
                    </div>

                    <div class="col-12 payment-verification-message">
                        <span i18n="Payment Method verification message" i18n>
                            Please provide us one of the following documents for verification. We
                            will email you within 1-3 business days when verification is complete.
                        </span>
                        <br />
                        <span class="bold" i18n>
                            Voided Check / Bank Account Statement / Bank Letter
                        </span>
                    </div>
                    <div class="col-12">
                        <lsq-file-picker
                            [placeholderText]="filePickerPlaceholderText"
                            [hideRemoveButton]="
                                (addIntlBankAccountFormService.fileUpload$ | async)
                                    ?.uploadHasStarted
                            "
                            [uploadErrorMessage]="
                                addIntlBankAccountFormService.uploadErrorMessage$ | async
                            "
                            [fileUpload]="addIntlBankAccountFormService.fileUpload$ | async"
                            (fileSourceValueChanged)="
                                addIntlBankAccountFormService.handleFileSelected($event)
                            "
                            (fileSourceElementChanged)="
                                addIntlBankAccountFormService.setFileSource($event)
                            "
                        >
                        </lsq-file-picker>
                    </div>
                </form>
            </div>
        </div>
    `
})
export class AddIntlBankAccountFormComponent implements OnInit {
    @Input() bankPaymentMethod!: SupportedBankPaymentMethod;

    public usEnum = SupportedBankCountry.UNITED_STATES;
    public chinaEnum = SupportedBankCountry.CHINA;
    public indiaEnum = SupportedBankCountry.INDIA;
    public canadaEnum = SupportedBankCountry.CANADA;

    public addBankAccountFormGroup$: Observable<FormGroup>;
    public readonly icons = Icons;
    public readonly accountNumberMaxLength = 256;
    public readonly usaRoutingNumberLength = 9;
    public readonly filePickerPlaceholderText = this._i18n('Attach Document');

    private routingNumberSubscription: Subscription;

    constructor(
        public addIntlBankAccountFormService: AddIntlBankAccountFormService,
        public addBankAccountFormService: AddBankAccountFormService,
        private _i18n: I18n
    ) {}

    public ngOnInit(): void {
        this.addIntlBankAccountFormService.resetFileSelected();
        this.addBankAccountFormGroup$ = this.addIntlBankAccountFormService.formGroup$;
        this.addBankAccountFormGroup$.subscribe((form) => {
            this.updateForm(form);
        });
    }

    public updateForm(form: FormGroup) {
        const routingNumberControl = form.get('routingNumber');
        const swiftCodeControl = form.get('swiftCode');
        const stateControl = form.get('state');
        const postalCodeControl = form.get('postal');

        if (
            this.bankPaymentMethod &&
            (this.bankPaymentMethod.country === SupportedBankCountry.CHINA ||
                this.bankPaymentMethod.country === SupportedBankCountry.INDIA)
        ) {
            routingNumberControl.setValidators([
                Validators.required,
                LsqValidators.exactLength(12)
            ]);
        } else if (
            this.bankPaymentMethod &&
            this.bankPaymentMethod.country === SupportedBankCountry.CANADA
        ) {
            routingNumberControl.setValidators([Validators.required, LsqValidators.trnoValidator]);
        } else if (
            this.bankPaymentMethod &&
            this.bankPaymentMethod.country === SupportedBankCountry.UNITED_STATES &&
            this.bankPaymentMethod.transferType === SupportedBankTransferType.WIRE
        ) {
            routingNumberControl.setValidators([
                Validators.required,
                LsqValidators.exactLength(this.usaRoutingNumberLength)
            ]);
        } else {
            routingNumberControl.clearValidators();
        }

        if (
            this.bankPaymentMethod &&
            this.bankPaymentMethod.country === SupportedBankCountry.UNITED_STATES &&
            this.bankPaymentMethod.transferType === SupportedBankTransferType.WIRE
        ) {
            swiftCodeControl.clearValidators();
            stateControl.setValidators([Validators.required]);
            postalCodeControl.setValidators([Validators.required]);

            this.routingNumberSubscription = routingNumberControl.valueChanges.subscribe(
                async (routingNumber: string) => {
                    if (routingNumber?.length === 9) {
                        await this.getBankNameAndHandleResponse(routingNumber, form);
                        form.get('routingNumber')!.updateValueAndValidity({ emitEvent: false });
                    }
                }
            );
        } else {
            swiftCodeControl.setValidators([
                Validators.required,
                LsqValidators.minLength(8),
                LsqValidators.maxLength(11)
            ]);
            stateControl.clearValidators();
            postalCodeControl.clearValidators();

            if (this.routingNumberSubscription != null) {
                this.routingNumberSubscription.unsubscribe();
            }
        }

        routingNumberControl.reset();
        swiftCodeControl.reset();
        stateControl.reset();
        postalCodeControl.reset();
    }

    public async getBankNameAndHandleResponse(
        routingNumber: string,
        formGroup: FormGroup
    ): Promise<void> {
        if (
            this.bankPaymentMethod &&
            this.bankPaymentMethod.country !== SupportedBankCountry.UNITED_STATES
        ) {
            return;
        }
        try {
            const response = await fetch(`${ROUTING_NUMBER_INFO_URL}${routingNumber}`);
            const responseBody = await response.json();
            const bankName = responseBody.customer_name;
            formGroup.patchValue({ bankName });
        } catch (error) {
            formGroup.get('bank-name')!.enable();
        }
    }

    public async ngOnChanges(changes: SimpleChanges) {
        if (this.addBankAccountFormGroup$) {
            const formGroup = await this.addBankAccountFormGroup$.pipe(first()).toPromise();
            this.updateForm(formGroup);
        }
    }
}
